import { createSlice } from "@reduxjs/toolkit";
import { localStorageKeys, statusConst } from "../../const";
import { loadPermissions, storeAuthDetails } from "./user-actions";
import { fetchCurrentCompany } from "../../auth/authHelper";

const initialState = {
  username: localStorage.getItem(localStorageKeys.USER.USERNAME),
  authenticated: !!localStorage.getItem(localStorageKeys.ACCESS_TOKEN),
  company: [],
  types: [],
  companyIds: [],
  preference: {},
  currentCompany: -1,
  permissions: {},
  roles: [],
  error: null,
  needAuth: false,
  authStatus: statusConst.IDLE,
  permissionStatus: statusConst.IDLE,
  userDetails: {},
  isLoading: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserDetails(state, action) {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateProfilePicture(state, action) {
      if (state.userDetails) {
        state.userDetails = {
          ...state.userDetails,
          uploadDocumentSeq: action.payload,
        };
      }
    },
    setUserCompany(state, action) {
      return {
        ...state,
        currentCompany: action.payload,
      };
    },
    setError(state, action) {
      return {
        ...state,
        error: action.payload,
      };
    },
    setNeedAuth(state, action) {
      return {
        ...state,
        needAuth: action.payload,
      };
    },
    setAuthStatus(state, action) {
      return {
        ...state,
        authStatus: action.payload,
      };
    },
    // Token refresh failed - the user has to authenticate again. Reset every
    // auth derived slice (permissions, companies, ...) so no screen renders
    // against half loaded data while AuthGuard routes back to /login.
    sessionExpired(state) {
      return {
        ...initialState,
        authenticated: false,
        needAuth: true,
        error: state.error,
        permissionStatus: statusConst.FAILURE,
      };
    },
    clearAuthData(state, action) {
      if (action.payload) {
        return { ...initialState, authenticated: false };
      } else {
        return { ...initialState, error: state.error, authenticated: false };
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadPermissions.pending, (state) => {
        console.log("LOAD PERMISSION PENDING");
        state.isLoading = true;
        state.permissionStatus = statusConst.PENDING;
      })
      .addCase(loadPermissions.fulfilled, (state, action) => {
        const payload = action.payload;
        const companies = payload?.companyIds ?? [];
        state.isLoading = false;
        state.permissionStatus = statusConst.SUCCESS;
        state.permissions = payload?.permissions ?? {};
        state.types = payload?.types ?? [];
        state.userDetails = payload?.userDetails ?? {};
        state.company = payload?.company ?? [];
        state.companyIds = companies;
        state.currentCompany = fetchCurrentCompany(companies);
      })
      .addCase(loadPermissions.rejected, (state) => {
        state.isLoading = false;
        state.permissionStatus = statusConst.FAILURE;
      })
      .addCase(storeAuthDetails.pending, (state) => {
        state.isLoading = true;
        state.permissionStatus = statusConst.PENDING;
      })
      .addCase(storeAuthDetails.fulfilled, (state, action) => {
        const payload = action.payload ?? {};
        Object.assign(state, {
          ...payload,
          permissions: payload.permissions ?? {},
          company: payload.company ?? [],
          companyIds: payload.companyIds ?? [],
          types: payload.types ?? [],
          userDetails: payload.userDetails ?? {},
        });
      })
      .addCase(storeAuthDetails.rejected, (state) => {
        state.isLoading = false;
        state.permissionStatus = statusConst.FAILURE;
      });
  },
});

export const userActions = userSlice.actions;
export default userSlice;
